// @ts-nocheck
import { IconUser, IconStar, IconTrendingUp } from '@tabler/icons-react';
import { Avatar, AvatarGroup, Card, Code, Divider, Group, Stack, Stat, Table, Text, Timeline, Title } from '@/components/ui';
import { mockUsers, mockTableData, mockTimelineEvents } from '../mockData';

export default function DataDisplayPage() {
  return (
    <Stack gap="xl">
      {/* Page Header */}
      <Stack gap="xs">
        <Title order={2}>Data Display Components</Title>
        <Text c="dimmed" size="sm">
          Components for displaying data, including cards, avatars, tables, stats, and timelines
        </Text>
      </Stack>

      {/* Card Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Card</Title>
            <Text size="sm" c="dimmed">
              Flexible container component with multiple variants and hover effects
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Basic Card:</Text>
            <Card withBorder p="md" style={{ maxWidth: 400 }}>
              <Stack gap="xs">
                <Title order={5}>Default Card</Title>
                <Text size="sm" c="dimmed">
                  This is a basic card with border, padding, and content
                </Text>
              </Stack>
            </Card>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Card withBorder p="md">
  <Stack gap="xs">
    <Title order={5}>Default Card</Title>
    <Text size="sm" c="dimmed">
      This is a basic card with border, padding, and content
    </Text>
  </Stack>
</Card>`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <Text size="sm" fw={500}>Gradient Card with Hover Effect:</Text>
            <Card variant="gradient" gradientVariant="primary" p="md" hoverEffect="glow" style={{ maxWidth: 400 }}>
              <Stack gap="xs">
                <Title order={5} c="white">
                  Gradient Card
                </Title>
                <Text size="sm" c="white" opacity={0.9}>
                  Gradient background with glow hover effect
                </Text>
              </Stack>
            </Card>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Card variant="gradient" gradientVariant="primary" p="md" hoverEffect="glow">
  <Stack gap="xs">
    <Title order={5} c="white">Gradient Card</Title>
    <Text size="sm" c="white" opacity={0.9}>
      Gradient background with glow hover effect
    </Text>
  </Stack>
</Card>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • variant - 'default' | 'gradient'
              <br />
              • gradientVariant - 'primary' | 'secondary' | 'accent' (when variant="gradient")
              <br />
              • hoverEffect - 'lift' | 'glow' | 'scale'
              <br />
              • withBorder - boolean to add border
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Avatar Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Avatar</Title>
            <Text size="sm" c="dimmed">
              Display user profile pictures or initials with various sizes and styles
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Basic Avatars:</Text>
            <Group>
              <Avatar src={mockUsers[0].avatar} alt={mockUsers[0].name} />
              <Avatar src={mockUsers[1].avatar} alt={mockUsers[1].name} size="lg" />
              <Avatar name={mockUsers[2].name} color="blue" />
              <Avatar name={mockUsers[3].name} color="green" variant="filled" />
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Avatar src="/avatars/user.jpg" alt="John Doe" />
<Avatar src="/avatars/user.jpg" alt="John Doe" size="lg" />
<Avatar name="John Doe" color="blue" />
<Avatar name="Jane Smith" color="green" variant="filled" />`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <Text size="sm" fw={500}>Sizes:</Text>
            <Group align="center">
              <Avatar src={mockUsers[0].avatar} size="xs" />
              <Avatar src={mockUsers[0].avatar} size="sm" />
              <Avatar src={mockUsers[0].avatar} size="md" />
              <Avatar src={mockUsers[0].avatar} size="lg" />
              <Avatar src={mockUsers[0].avatar} size="xl" />
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Avatar src="/avatar.jpg" size="xs" />
<Avatar src="/avatar.jpg" size="sm" />
<Avatar src="/avatar.jpg" size="md" />
<Avatar src="/avatar.jpg" size="lg" />
<Avatar src="/avatar.jpg" size="xl" />`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <Text size="sm" fw={500}>Avatar Group:</Text>
            <AvatarGroup
              avatars={mockUsers.slice(0, 6).map((user) => ({
                src: user.avatar,
                name: user.name,
              }))}
              max={4}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<AvatarGroup
  avatars={users.map((user) => ({
    src: user.avatar,
    name: user.name,
  }))}
  max={4}
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Stat Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Stat</Title>
            <Text size="sm" c="dimmed">
              Display statistics and metrics with labels, values, and optional trends
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Basic Stat:</Text>
            <Group grow>
              <Stat label="Total Users" value="1,234" />
              <Stat label="Revenue" value="$45,678" />
              <Stat label="Active Projects" value="23" />
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Stat label="Total Users" value="1,234" />
<Stat label="Revenue" value="$45,678" />
<Stat label="Active Projects" value="23" />`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <Text size="sm" fw={500}>With Icons and Trends:</Text>
            <Group grow>
              <Stat
                label="Total Users"
                value="1,234"
                icon={<IconUser size={20} />}
                trend={{ value: 12, type: 'increase' }}
                trendLabel="vs last month"
              />
              <Stat
                label="Rating"
                value="4.8"
                icon={<IconStar size={20} />}
                trend={{ value: 0.3, type: 'increase' }}
                trendLabel="vs last week"
              />
              <Stat
                label="Growth"
                value="23%"
                icon={<IconTrendingUp size={20} />}
                trend={{ value: 5, type: 'increase' }}
                trendLabel="this quarter"
              />
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Stat
  label="Total Users"
  value="1,234"
  icon={<IconUser size={20} />}
  trend={{ value: 12, type: 'increase' }}
  trendLabel="vs last month"
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Table Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Table</Title>
            <Text size="sm" c="dimmed">
              Display tabular data with Mantine's Table component
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Basic Table:</Text>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Department</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {mockTableData.slice(0, 5).map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td>{row.name}</Table.Td>
                    <Table.Td>{row.email}</Table.Td>
                    <Table.Td>{row.role}</Table.Td>
                    <Table.Td>{row.department}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Table striped highlightOnHover withTableBorder>
  <Table.Thead>
    <Table.Tr>
      <Table.Th>Name</Table.Th>
      <Table.Th>Email</Table.Th>
      <Table.Th>Role</Table.Th>
    </Table.Tr>
  </Table.Thead>
  <Table.Tbody>
    {data.map((row) => (
      <Table.Tr key={row.id}>
        <Table.Td>{row.name}</Table.Td>
        <Table.Td>{row.email}</Table.Td>
        <Table.Td>{row.role}</Table.Td>
      </Table.Tr>
    ))}
  </Table.Tbody>
</Table>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Timeline Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Timeline</Title>
            <Text size="sm" c="dimmed">
              Display chronological events with titles, descriptions, and timestamps
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example Timeline:</Text>
            <Timeline
              items={mockTimelineEvents.slice(0, 4).map((event) => ({
                title: event.title,
                description: event.description,
                timestamp: event.timestamp,
              }))}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Timeline
  items={[
    {
      title: 'Project created',
      description: 'Website Redesign project was created',
      timestamp: new Date('2024-01-10T09:00:00'),
    },
    {
      title: 'Task assigned',
      description: 'New task assigned to John Doe',
      timestamp: new Date('2024-01-11T10:30:00'),
    },
    // ... more items
  ]}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • items - Array of timeline items with title, description, timestamp
              <br />
              • Each item can include optional icon and color
            </Text>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
